// djin-cloud/web/src/components/charts/echarts/GroupedBarChart.tsx

import ReactECharts from 'echarts-for-react';
import { use as echartsUse } from 'echarts/core';
import type { EChartsCoreOption } from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent, DataZoomComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { DefaultLabelFormatterCallbackParams } from 'echarts';
import { GAMMA_COLORS } from '../../../theme/gamma-echarts-theme';

echartsUse([BarChart, GridComponent, TooltipComponent, LegendComponent, DataZoomComponent, CanvasRenderer]);

export interface GroupedBarChartDataPoint {
  label: string;
  consumption: number;
  generation: number;
  bess_throughput: number;
}

interface GroupedBarChartProps {
  data: GroupedBarChartDataPoint[];
  height?: number;
  onBarClick?: (label: string) => void;
}

export function GroupedBarChart({ data, height = 320, onBarClick }: GroupedBarChartProps) {
  const labels = data.map((d) => d.label);

  const option: EChartsCoreOption = {
    animation: true,
    grid: { left: 60, right: 20, top: 40, bottom: 50 },
    legend: {
      top: 8,
      data: ['Consumption', 'Generation', 'BESS Throughput'],
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: DefaultLabelFormatterCallbackParams | DefaultLabelFormatterCallbackParams[]) => {
        if (!Array.isArray(params) || params.length === 0) return '';
        const label = (params[0] as DefaultLabelFormatterCallbackParams & { axisValueLabel?: string }).axisValueLabel ?? params[0].name;
        const lines = params.map((p: DefaultLabelFormatterCallbackParams) => {
          const marker = `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:8px;height:8px;background-color:${p.color};"></span>`;
          return `${marker}<span style="font-family:monospace">${p.seriesName}: ${Number(p.value).toFixed(1)} kWh</span>`;
        });
        return `<div style="font-size:12px"><strong>${label}</strong><br/>${lines.join('<br/>')}</div>`;
      },
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: { rotate: labels.length > 15 ? 45 : 0 },
    },
    yAxis: {
      type: 'value',
      name: 'kWh',
      nameTextStyle: { color: GAMMA_COLORS.textMuted, fontSize: 11 },
    },
    dataZoom: [{ type: 'inside', xAxisIndex: 0 }],
    series: [
      {
        name: 'Consumption',
        type: 'bar' as const,
        barGap: '10%',
        itemStyle: { color: GAMMA_COLORS.blue, borderRadius: [2, 2, 0, 0] },
        data: data.map((d) => d.consumption),
      },
      {
        name: 'Generation',
        type: 'bar' as const,
        itemStyle: { color: GAMMA_COLORS.green, borderRadius: [2, 2, 0, 0] },
        data: data.map((d) => d.generation),
      },
      {
        name: 'BESS Throughput',
        type: 'bar' as const,
        itemStyle: { color: GAMMA_COLORS.cyan, borderRadius: [2, 2, 0, 0] },
        data: data.map((d) => d.bess_throughput),
      },
    ],
  };

  const onEvents = onBarClick
    ? {
        click: (params: { name: string }) => {
          onBarClick(params.name);
        },
      }
    : undefined;

  return (
    <ReactECharts
      option={option}
      style={{ height }}
      theme="gamma"
      notMerge={false}
      lazyUpdate={true}
      onEvents={onEvents}
    />
  );
}
